import { ref } from 'vue';

export function useDocumentHandler() {
    const passportUploadError = ref('');
    const licenseUploadError = ref('');
    const vhfLicenseUploadError = ref('');
    

    const pendingPassportFiles = ref<File[]>([]);
    const pendingLicenseFiles = ref<File[]>([]);
    const pendingVHFFiles = ref<File[]>([]);

    function getUploadFn(type: 'passport' | 'license' | 'vhf') {
        return (file: File) => Promise.resolve(file);
    }
    
    function getErrorRef(type: 'passport' | 'license' | 'vhf') {
        if (type === 'passport') return passportUploadError;
        if (type === 'license') return licenseUploadError;
        return vhfLicenseUploadError; // default for 'vhf'
    }

    function getTargetRef(type: 'passport' | 'license' | 'vhf') {
        if (type === 'passport') return pendingPassportFiles;
        if (type === 'license') return pendingLicenseFiles;
        if (type === 'vhf') return pendingVHFFiles;
    }

    function validateFile(file: File, errorRef: any, allowedTypes: string[], maxSizeMB: number) {
        const isPdf = file.type === 'application/pdf';
        const isImage = file.type.startsWith('image/');
        const isValidType = isPdf || isImage || allowedTypes.includes(file.type);
        const isValidSize = file.size / 1024 / 1024 <= maxSizeMB;

        if (!isValidType) {
            errorRef.value = 'Invalid file type.';
            return false;
        }

        if (!isValidSize) {
            errorRef.value = 'File is too large.';
            return false;
        }

        errorRef.value = '';
        return true;
    }

    function processFileUpload(
        file: any,
        uploadFn: (file: File) => Promise<any>,
        targetRef: any,
        options: { onError?: () => void }
    ) {
        const realFile: File | null =
            file instanceof Blob ? file :
            file?.raw instanceof Blob ? file.raw :
            file?.file instanceof Blob ? file.file : null;

        if (!realFile) {
            options.onError?.();
            return;
        }

        uploadFn(realFile)
            .then(() => {
                // ensure the dropped/selected entry appears in the UI list
                if (!targetRef.value.includes(file)) {
                    targetRef.value.push(file);
                }
            })
            .catch(() => {
                options.onError?.();
            });
    }

    function validateAndProcessFile(
        file: any,
        type: 'passport' | 'license' | 'vhf',
        listId?: string,
        crewId?: string
    ) {
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        const maxSizeMB = 2;
        const errorRef = getErrorRef(type);

        // Validate using a normalized File
        const maybeFile = file instanceof Blob ? file : file?.raw instanceof Blob ? file.raw : file?.file instanceof Blob ? file.file : null;
        if (!maybeFile) {
            errorRef.value = 'Invalid file input.';
            return;
        }
        if (!validateFile(maybeFile as File, errorRef, allowedTypes, maxSizeMB)) return;

        const targetRef = getTargetRef(type);
        const baseUploadFn = getUploadFn(type);
        const uploadFn = (f: File) => {
            if (listId && crewId) {
                return (baseUploadFn as any)(listId, crewId, f);
            }
            return baseUploadFn(f);
        };

        processFileUpload(file, uploadFn, targetRef, {
            onError: () => {
                errorRef.value = `${type.toUpperCase()} upload failed.`;
            }
        });
    }

    function handlePendingFileInput(input: Event | any[], type: 'passport' | 'license' | 'vhf') {
        const files: any[] = Array.isArray(input)
            ? input
            : (() => {
                  const el = (input as Event).target as HTMLInputElement | null;
                  return el?.files ? Array.from(el.files) : [];
              })();

        if (!files.length) return;

        files.forEach((f) => {
            // Normalize to a File for validation; pass the original to processor (it can unwrap .raw/.file)
            const maybeFile = f instanceof Blob ? f : f?.raw instanceof Blob ? f.raw : f?.file instanceof Blob ? f.file : null;
            if (!maybeFile) return;

            const errorRef = getErrorRef(type);
            const ok = validateFile(maybeFile as File, errorRef, ['image/jpeg', 'image/png', 'application/pdf'], 2);
            if (!ok) return;

            const targetRef = getTargetRef(type);
            // Push the original object for consistency with UI library (we will unwrap when converting)
            targetRef.value.push(f);
        });
    }

    async function fileToBase64Object(file: any) {
        return new Promise((resolve, reject) => {
            // Normalize possible shapes coming from UI libraries
            const maybeFile =
                file instanceof Blob ? file :
                file?.raw instanceof Blob ? file.raw :
                file?.file instanceof Blob ? file.file : null;

            if (!maybeFile) {
                return reject(new TypeError('Expected a File/Blob-like object'));
            }

            const reader = new FileReader();
            reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                resolve({
                    file: base64,
                    fileName: (maybeFile as File).name,
                    fileType: (maybeFile as File).type,
                    size: (maybeFile as File).size,
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(maybeFile);
        });
    }

    async function convertFilesToBase64Array(files: any[]) {
        const normalized = files
            .map((f) => (f instanceof Blob ? f : f?.raw instanceof Blob ? f.raw : f?.file instanceof Blob ? f.file : null))
            .filter((f): f is File => !!f);
        return Promise.all(normalized.map(fileToBase64Object));
    }

    return {
        passportUploadError,
        licenseUploadError,
        vhfLicenseUploadError,
        pendingPassportFiles,
        pendingLicenseFiles,
        pendingVHFFiles,
        
        validateAndProcessFile,
        convertFilesToBase64Array,
        handlePendingFileInput,
    };
}